import { characterActions } from '../actions';
import { ContainerTypeEnum } from '../engine/Container';
import { CHARACTER_ENTITY_TYPE_ID } from '../engine/Core';
import { DefinitionHacks } from '../engine/Definition';
import { ItemAccessors, ItemNotes, ItemUtils, ItemValidators } from '../engine/Item';
import { rulesEngineSelectors } from '../selectors';
import { PartyManager } from './PartyManager';
export class ItemManager extends PartyManager {
    constructor(params) {
        super(params);
        // Actions
        this.handleAdd = ({ amount, containerDefinitionKey }, onSuccess, onError) => {
            const isShared = this.container.isSharedOtherContainerDefinitionKey(containerDefinitionKey);
            this.dispatch(characterActions.itemCreate(this.item, amount, this.handleAcceptOnSuccess(isShared, onSuccess), this.handleRejectOnError(onError), containerDefinitionKey));
        };
        this.handleRemove = (onSuccess, onError) => {
            const isShared = this.isShared();
            if (this.isCustom()) {
                this.dispatch(characterActions.customItemDestroy(this.getDefinitionId(), this.getMappingId(), isShared ? this.getPartyId() : null, this.handleAcceptOnSuccess(isShared, onSuccess)));
            }
            else {
                const isContainer = this.isContainer();
                const infusion = this.getInfusion();
                this.dispatch(characterActions.itemDestroy(this.getMappingId(), isContainer && infusion ? false : true, this.handleAcceptOnSuccess(isShared, onSuccess)));
            }
        };
        this.handleMove = ({ containerDefinitionKey }, onSuccess, onError) => {
            const definitionKeyId = DefinitionHacks.hack__getDefinitionKeyId(containerDefinitionKey);
            const definitionKeyType = DefinitionHacks.hack__getDefinitionKeyType(containerDefinitionKey);
            if (definitionKeyId && definitionKeyType) {
                const isShared = this.container.isSharedOtherContainerDefinitionKey(containerDefinitionKey) || this.isShared();
                this.dispatch(characterActions.itemMoveSet(this.getMappingId(), definitionKeyId, definitionKeyType, this.handleAcceptOnSuccess(isShared, onSuccess)));
            }
        };
        this.handleEquip = (onSuccess, onError) => {
            const characterId = rulesEngineSelectors.getId(this.state);
            this.dispatch(characterActions.itemEquippedSet(ItemAccessors.getMappingId(this.item), true, characterId, CHARACTER_ENTITY_TYPE_ID, this.handleAcceptOnSuccess(this.isShared(), onSuccess)));
        };
        this.handleUnequip = (onSuccess, onError) => {
            this.dispatch(characterActions.itemEquippedSet(ItemAccessors.getMappingId(this.item), false, null, null, this.handleAcceptOnSuccess(this.isShared(), onSuccess)));
        };
        // Accessors
        this.getUniqueKey = () => {
            return `${ItemAccessors.getUniqueKey(this.item)}-${this.container.getDefinitionKey()}`;
        };
        this.isEquipped = () => ItemAccessors.isEquipped(this.item);
        this.getMappingId = () => ItemAccessors.getMappingId(this.item);
        this.getType = () => ItemAccessors.getType(this.item);
        this.isHexWeapon = () => ItemAccessors.isHexWeapon(this.item);
        this.isPactWeapon = () => ItemAccessors.isPactWeapon(this.item);
        this.isDedicatedWeapon = () => ItemAccessors.isDedicatedWeapon(this.item);
        this.isOffhand = () => ItemAccessors.isOffhand(this.item);
        this.getBaseArmorName = () => ItemAccessors.getBaseArmorName(this.item);
        this.getSubType = () => ItemAccessors.getSubType(this.item);
        this.getQuantity = () => ItemAccessors.getQuantity(this.item);
        this.isStackable = () => ItemAccessors.isStackable(this.item);
        this.getWeight = () => ItemAccessors.getWeight(this.item);
        this.getCost = () => ItemAccessors.getCost(this.item);
        this.getAvatarUrl = () => ItemAccessors.getAvatarUrl(this.item);
        this.isContainer = () => ItemAccessors.isContainer(this.item);
        this.getName = () => ItemAccessors.getName(this.item);
        this.isCustom = () => ItemAccessors.isCustom(this.item);
        this.getDefinitionId = () => ItemAccessors.getDefinitionId(this.item);
        this.getInfusion = () => ItemAccessors.getInfusion(this.item);
        this.getContainerDefinitionKey = () => ItemAccessors.getContainerDefinitionKey(this.item);
        // Validator
        this.canEquipUnequipItem = () => {
            const isShared = this.isShared();
            const canEquip = ItemAccessors.canEquip(this.item);
            const isEquipped = ItemAccessors.isEquipped(this.item);
            if (isShared && canEquip) {
                if (this.isSharingTurnedOn() || (this.isSharingTurnedDeleteOnly() && isEquipped)) {
                    return isEquipped ? this.isEquippedToCurrentCharacter() : true;
                }
                return false;
            }
            return canEquip;
        };
        this.isArmorContract = () => ItemUtils.isArmorContract(this.item);
        this.isGearContract = () => ItemUtils.isGearContract(this.item);
        this.isShared = () => this.container.isShared();
        this.isEquippedToCurrentCharacter = () => {
            const characterId = rulesEngineSelectors.getId(this.state);
            return ItemValidators.isEquippedToCurrentCharacter(this.item, characterId);
        };
        // Utils
        this.generateContainerDefinitionKey = () => {
            return DefinitionHacks.hack__generateDefinitionKey(ContainerTypeEnum.ITEM, this.getMappingId());
        };
        this.getEquippedCharacterName = () => {
            var _a;
            const equippedEntityId = ItemAccessors.getEquippedEntityId(this.item);
            if (this.isEquippedToCurrentCharacter()) {
                return (_a = rulesEngineSelectors.getName(this.state)) !== null && _a !== void 0 ? _a : '';
            }
            if (equippedEntityId) {
                return this.getPartyMemberName(equippedEntityId);
            }
            return 'Hero with no name';
        };
        this.getNotes = () => {
            const weaponSpellDamageGroups = rulesEngineSelectors.getWeaponSpellDamageGroups(this.state);
            const ruleData = rulesEngineSelectors.getRuleData(this.state);
            const abilityLookup = rulesEngineSelectors.getAbilityLookup(this.state);
            const proficiencyBonus = rulesEngineSelectors.getProficiencyBonus(this.state);
            return ItemNotes.getNoteComponents(this.item, weaponSpellDamageGroups, ruleData, abilityLookup, proficiencyBonus);
        };
        this.getMetaText = () => {
            let metaItems = [];
            const type = this.getType();
            if (type !== null) {
                metaItems.push(type);
            }
            if (this.isHexWeapon()) {
                metaItems.push('Hex Weapon');
            }
            if (this.isPactWeapon()) {
                metaItems.push('Pact Weapon');
            }
            if (this.isDedicatedWeapon()) {
                metaItems.push('Dedicated Weapon');
            }
            if (this.isOffhand()) {
                metaItems.push('Dual Wield');
            }
            if (this.isArmorContract()) {
                let baseArmorName = this.getBaseArmorName();
                if (baseArmorName) {
                    metaItems.push(baseArmorName);
                }
            }
            else if (this.isGearContract()) {
                let subType = this.getSubType();
                if (subType) {
                    metaItems.push(subType);
                }
            }
            if (this.isEquipped() && !this.isEquippedToCurrentCharacter()) {
                metaItems.push(`Equipped by ${this.getEquippedCharacterName()}`);
            }
            return metaItems;
        };
        this.item = params.item;
        this.container = params.container;
    }
}
